import { useState, useEffect, useCallback, useRef } from 'react';
import { EsporaiInput, OnboardingStepLayout } from 'components';
import { validateUrlRaw } from 'utils/validUrl.js';

const OnboardingWebsiteStep = ({ value, onChange, onValidation, isProcessing = false, invalidURLError = false, userType = 'negocio' }) => {
  const [error, setError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(Boolean(value));
  const [loadingStep, setLoadingStep] = useState(0);
  const [hasCompletedProcessing, setHasCompletedProcessing] = useState(false);

  const isFirstRender = useRef(true);
  const prevValue = useRef(value);

  const loadingMessages = [
    "Estamos ingresando a tu sitio...",
    "Identificando industria...",
    "Analizando competidores...",
    "Encontrando mejores estrategias...",
    "Generando reporte..."
  ];

  const validateUrl = useCallback(() => {
    const { valid, error } = validateUrlRaw(value);
    setError(error);
    return valid;
  }, [value]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const isValid = validateUrl();
      onValidation(isValid);
      prevValue.current = value;
      return;
    }

    if (value !== prevValue.current) {
      const isValid = validateUrl();
      onValidation(isValid);
      prevValue.current = value;
    }
  }, [value, validateUrl, onValidation]);

  // Loading animation effect - triggered by isProcessing prop
  useEffect(() => {
    if (isProcessing) {
      setHasCompletedProcessing(false);
      const timer = setTimeout(() => {
        if (loadingStep < loadingMessages.length - 1) {
          setLoadingStep(prev => prev + 1);
        }
      }, 2000);

      return () => clearTimeout(timer);
    } else {
      setLoadingStep(0);
      setHasCompletedProcessing(false);
    }
  }, [isProcessing, loadingStep, loadingMessages.length]);

  const handleInputChange = (val) => {
    if (!hasStartedTyping && val) {
      setHasStartedTyping(true);
    }
    onChange(val);
  };

  const handleFocus = () => {
    setInputFocused(true);
  };

  const handleBlur = () => {
    setInputFocused(false);
  };

  const displayError = inputFocused && hasStartedTyping ? error : null;

  const isAgency = userType === 'agencia';
  const title = isAgency
    ? '¿Cuál es la URL de alguno de tus clientes?'
    : '¿Cuál es la URL de tu negocio?';
  const subtitle = isAgency
    ? 'Ingresá la web de uno de tus clientes para que podamos entender mejor el negocio y armar estrategias a medida.'
    : '¡Colocando tu página web vamos a poder entender mejor de tu negocio!';

  return (
    <OnboardingStepLayout
      title={title}
      subtitle={subtitle}
    >
      <EsporaiInput
        autoFocus
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={isAgency ? 'www.cliente.com' : 'www.minegocio.com'}
        error={invalidURLError ? "¿Estás seguro que la URL es correcta? Revisala por las dudas. Si estás seguro, puedes continuar." : displayError}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />

      {invalidURLError && (
        <div className="my-2 py-2"></div>
      )}
      
      {/* Loading Animation */}
      {(!invalidURLError && (isProcessing || hasCompletedProcessing)) && (
        <div className="mt-2 space-y-3 w-80">
          {loadingMessages.map((message, index) => (
            <div
              key={index}
              className={`flex items-center space-x-2 transition-all duration-500 ${
                index <= loadingStep 
                  ? 'opacity-100 transform translate-x-0' 
                  : 'opacity-0 transform translate-x-4'
              }`}
            >
              <span className="text-sm text-gray-600 font-mono">{message}</span>
              {index < loadingStep && (
                <span className="text-green-500 text-lg">✅</span>
              )}
              {index === loadingStep && (isProcessing || (hasCompletedProcessing && index === loadingMessages.length - 1)) && (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              )}
              {index === loadingStep && hasCompletedProcessing && index < loadingMessages.length - 1 && (
                <span className="text-green-500 text-lg">✅</span>
              )}
            </div>
          ))}
        </div>
      )}
    </OnboardingStepLayout>
  );
};

export default OnboardingWebsiteStep;
